import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	GitMerge,
	Plus,
	RefreshCw,
	Trash2,
	TrendingDown,
	Unlink,
	UserPlus,
	Users,
} from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ActivityTimeline } from "@/components/crm/activity-timeline";
import { AddExistingContactDialog } from "@/components/crm/add-existing-contact-dialog";
import { CompanyGroupCard } from "@/components/crm/company-group-card";
import { ContactFormDialog } from "@/components/crm/contact-form";
import { CrmLoadError } from "@/components/crm/crm-page";
import { InlineField, InlineRow } from "@/components/crm/inline-field";
import { MergeDialog } from "@/components/crm/merge-dialog";
import { OpportunityFormDialog } from "@/components/crm/opportunity-form";
import { OwnerSelect } from "@/components/crm/owner-select";
import { PlatformOrgPicker } from "@/components/crm/platform-org-picker";
import { StageBadge } from "@/components/crm/stage-badge";
import {
	OrgTypeBadge,
	PlatformStageBadge,
	ToneBadge,
} from "@/components/crm/status-badges";
import { TagInput } from "@/components/crm/tag-input";
import { TasksPanel } from "@/components/crm/task-list";
import { UpcomingMeetings } from "@/components/crm/upcoming-meetings";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccessProfile } from "@/hooks/use-admin-auth";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import {
	CRM_ORGANIZATION_SIZES,
	CRM_ORGANIZATION_TYPES,
} from "@/lib/crm/constants";
import { contactName, decodeCrmError, formatNumber } from "@/lib/crm/format";
import { CRM_STALE_TIME, invalidateCrmData } from "@/lib/crm/queries";
import type {
	CrmOrganization,
	CrmOrganizationSize,
	CrmOrganizationType,
} from "@/lib/crm/types";
import { formatMrr } from "@/lib/sales-format";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/organizations/$organizationId")({
	component: CompanyPage,
});

const NO_SIZE = "__none";

/** Header buttons on a card background, so they stand out from the page. */
const HEADER_OUTLINE = "bg-card dark:bg-card";

type OrganizationPatch = Parameters<ReturnType<typeof useCompanyUpdate>>[0];

const useCompanyUpdate = (id: string) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const update = useMutation(
		orpc.admin.crm.organizations.update.mutationOptions(),
	);
	return async (
		patch: Parameters<typeof update.mutateAsync>[0]["patch"],
	): Promise<void> => {
		try {
			await update.mutateAsync({ id, patch });
			await invalidateCrmData(queryClient);
		} catch (error) {
			const decoded = decodeCrmError(error);
			toast.error(
				decoded.code === "CONFLICT"
					? t("admin:crm.contact_form.duplicate_company")
					: (decoded.message ?? t("admin:crm.errors.generic")),
			);
			throw error;
		}
	};
};

function CompanyPage(): ReactElement {
	const { organizationId } = Route.useParams();
	const { t, i18n } = useTranslation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { canManage, canAdmin } = useCrmPermissions();
	const { profile } = useAccessProfile();
	const updateCompany = useCompanyUpdate(organizationId);
	const [contactOpen, setContactOpen] = useState(false);
	const [existingContactOpen, setExistingContactOpen] = useState(false);
	const [dealOpen, setDealOpen] = useState(false);
	const [mergeOpen, setMergeOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [linking, setLinking] = useState(false);
	// "Whole group": deals and the timeline span the corporate group (S19).
	const [wholeGroup, setWholeGroup] = useState(false);

	const { data, isLoading, isError } = useQuery({
		...orpc.admin.crm.organizations.get.queryOptions({
			input: { id: organizationId },
		}),
		staleTime: CRM_STALE_TIME,
	});
	const { data: dealsData } = useQuery({
		...orpc.admin.crm.opportunities.list.queryOptions({
			input: {
				organizationId,
				includeGroup: wholeGroup,
				limit: 100,
				offset: 0,
			},
		}),
		enabled: canManage,
		staleTime: CRM_STALE_TIME,
	});
	const lookup = useMutation(
		orpc.admin.crm.organizations.lookupRegistry.mutationOptions(),
	);
	const link = useMutation(orpc.admin.crm.organizations.link.mutationOptions());
	const unlink = useMutation(
		orpc.admin.crm.organizations.unlink.mutationOptions(),
	);
	const remove = useMutation(
		orpc.admin.crm.organizations.delete.mutationOptions(),
	);

	if (isLoading) {
		return (
			<div className="space-y-4">
				<Skeleton className="h-10 w-1/2" />
				<Skeleton className="h-64" />
			</div>
		);
	}
	if (isError || !data) return <CrmLoadError />;

	const org: CrmOrganization = data.organization;
	const save = (patch: OrganizationPatch): Promise<void> =>
		updateCompany(patch);
	const saveQuietly = (patch: OrganizationPatch): void => {
		save(patch).catch(() => undefined);
	};
	const textOrNull = (value: string): string | null => value.trim() || null;

	const refreshFromRegistry = async (): Promise<void> => {
		if (!org.businessId) return;
		try {
			const { companies } = await lookup.mutateAsync({
				businessId: org.businessId,
			});
			const [hit] = companies;
			if (!hit) {
				toast.info(t("admin:crm.company.lookup_none"));
				return;
			}
			await save({
				address: hit.address,
				industry: hit.industry,
				industryCode: hit.industryCode,
				website: org.website ?? hit.website,
				country: org.country ?? "FI",
			});
			toast.success(t("admin:crm.company.registry_refreshed"));
		} catch {
			// save() already reported it.
		}
	};

	const runLink = async (
		action: "link" | "unlink",
		platformOrganizationId: string,
	): Promise<void> => {
		try {
			const mutation = action === "link" ? link : unlink;
			await mutation.mutateAsync({ id: org.id, platformOrganizationId });
			await invalidateCrmData(queryClient);
			setLinking(false);
		} catch (error) {
			const decoded = decodeCrmError(error);
			toast.error(
				decoded.code === "CONFLICT"
					? t("admin:crm.platform.linked_elsewhere")
					: (decoded.message ?? t("admin:crm.errors.generic")),
			);
		}
	};

	const confirmDelete = async (): Promise<void> => {
		try {
			await remove.mutateAsync({ id: org.id });
			await invalidateCrmData(queryClient);
			toast.success(t("admin:crm.company.deleted"));
			void navigate({ to: "/crm/organizations" });
		} catch (error) {
			toast.error(
				decodeCrmError(error).message ?? t("admin:crm.errors.generic"),
			);
		}
	};

	const address = org.address ?? { street: null, postalCode: null, city: null };
	const deals = dealsData?.opportunities ?? [];
	const companyRef = { id: org.id, name: org.name, businessId: org.businessId };

	return (
		<div className="space-y-4">
			<Link
				to="/crm/organizations"
				className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:underline"
			>
				<ArrowLeft className="h-4 w-4" />
				{t("admin:crm.companies.title")}
			</Link>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h1 className="flex flex-wrap items-center gap-2 font-bold text-3xl">
						{org.name}
						<OrgTypeBadge type={org.type} />
					</h1>
					<p className="text-muted-foreground text-sm">
						{[org.businessId, org.industry, address.city]
							.filter(Boolean)
							.join(" · ")}
					</p>
				</div>
				{canManage && (
					<div className="flex flex-wrap gap-2">
						<Button onClick={() => setContactOpen(true)}>
							<UserPlus className="h-4 w-4" />
							{t("admin:crm.company.add_contact")}
						</Button>
						<Button
							variant="outline"
							className={HEADER_OUTLINE}
							onClick={() => setDealOpen(true)}
						>
							<Plus className="h-4 w-4" />
							{t("admin:crm.pipeline.new_deal")}
						</Button>
						{canAdmin && (
							<Button
								variant="outline"
								className={HEADER_OUTLINE}
								onClick={() => setMergeOpen(true)}
							>
								<GitMerge className="h-4 w-4" />
								{t("admin:crm.merge.button")}
							</Button>
						)}
						<Button
							variant="ghost"
							onClick={() => setDeleteOpen(true)}
							aria-label={t("admin:crm.company.delete")}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				)}
			</div>

			<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
				<div className="space-y-4">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-base">
								{t("admin:crm.company.firmographics")}
							</CardTitle>
							{canManage && org.businessId && (
								<Button
									variant="ghost"
									size="sm"
									disabled={lookup.isPending}
									onClick={() => void refreshFromRegistry()}
								>
									<RefreshCw
										className={`h-4 w-4 ${lookup.isPending ? "animate-spin" : ""}`}
									/>
									{t("admin:crm.company.refresh_registry")}
								</Button>
							)}
						</CardHeader>
						<CardContent>
							<dl>
								<InlineField
									label={t("admin:crm.company.name")}
									value={org.name}
									editable={canManage}
									onSave={(value) => save({ name: value.trim() || org.name })}
								/>
								<InlineField
									label={t("admin:crm.company.business_id")}
									value={org.businessId ?? ""}
									editable={canManage}
									onSave={(value) => save({ businessId: textOrNull(value) })}
								/>
								<InlineRow label={t("admin:crm.company.type")}>
									{canManage ? (
										<Select
											value={org.type}
											onValueChange={(value) =>
												saveQuietly({ type: value as CrmOrganizationType })
											}
										>
											<SelectTrigger
												className="h-8"
												aria-label={t("admin:crm.company.type")}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{CRM_ORGANIZATION_TYPES.map((value) => (
													<SelectItem key={value} value={value}>
														{t(`admin:crm.org_type.${value}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : (
										t(`admin:crm.org_type.${org.type}`)
									)}
								</InlineRow>
								<InlineField
									label={t("admin:crm.company.website")}
									value={org.website ?? ""}
									editable={canManage}
									onSave={(value) => save({ website: textOrNull(value) })}
								/>
								<InlineField
									label={t("admin:crm.company.domains")}
									value={org.domains.join(", ")}
									editable={canManage}
									placeholder="example.fi, example.com"
									onSave={(value) =>
										save({
											domains: value
												.split(/[,\s]+/)
												.map((domain) => domain.trim())
												.filter(Boolean),
										})
									}
								/>
								<InlineField
									label={t("admin:crm.company.street")}
									value={address.street ?? ""}
									editable={canManage}
									onSave={(value) =>
										save({ address: { ...address, street: textOrNull(value) } })
									}
								/>
								<InlineField
									label={t("admin:crm.company.postal_code")}
									value={address.postalCode ?? ""}
									editable={canManage}
									onSave={(value) =>
										save({
											address: { ...address, postalCode: textOrNull(value) },
										})
									}
								/>
								<InlineField
									label={t("admin:crm.company.city")}
									value={address.city ?? ""}
									editable={canManage}
									onSave={(value) =>
										save({ address: { ...address, city: textOrNull(value) } })
									}
								/>
								<InlineField
									label={t("admin:crm.company.country")}
									value={org.country ?? ""}
									editable={canManage}
									placeholder="FI"
									onSave={(value) =>
										save({ country: textOrNull(value)?.toUpperCase() ?? null })
									}
								/>
								<InlineField
									label={t("admin:crm.company.industry")}
									value={org.industry ?? ""}
									display={
										org.industry
											? `${org.industry}${org.industryCode ? ` (${org.industryCode})` : ""}`
											: undefined
									}
									editable={canManage}
									onSave={(value) => save({ industry: textOrNull(value) })}
								/>
								<InlineRow label={t("admin:crm.company.size")}>
									{canManage ? (
										<Select
											value={org.size ?? NO_SIZE}
											onValueChange={(value) =>
												saveQuietly({
													size:
														value === NO_SIZE
															? null
															: (value as CrmOrganizationSize),
												})
											}
										>
											<SelectTrigger
												className="h-8"
												aria-label={t("admin:crm.company.size")}
											>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value={NO_SIZE}>
													{t("admin:crm.common.not_set")}
												</SelectItem>
												{CRM_ORGANIZATION_SIZES.map((value) => (
													<SelectItem key={value} value={value}>
														{t(`admin:crm.org_size.${value}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									) : org.size ? (
										t(`admin:crm.org_size.${org.size}`)
									) : (
										"—"
									)}
								</InlineRow>
								<InlineField
									label={t("admin:crm.company.employees")}
									type="number"
									value={org.employeeCount?.toString() ?? ""}
									editable={canManage}
									onSave={(value) =>
										save({
											employeeCount: value.trim()
												? Math.max(0, Math.round(Number(value)))
												: null,
										})
									}
								/>
								<InlineField
									label={t("admin:crm.company.revenue")}
									type="number"
									value={
										org.revenue !== null
											? String(Math.round(org.revenue / 100))
											: ""
									}
									display={
										org.revenue !== null
											? `${new Intl.NumberFormat(i18n.language, {
													style: "currency",
													currency: org.revenueCurrency ?? "EUR",
													maximumFractionDigits: 0,
												}).format(
													org.revenue / 100,
												)}${org.revenueYear ? ` (${org.revenueYear})` : ""}`
											: undefined
									}
									editable={canManage}
									onSave={(value) =>
										save({
											revenue: value.trim()
												? Math.max(0, Math.round(Number(value) * 100))
												: null,
											revenueCurrency: org.revenueCurrency ?? "EUR",
										})
									}
								/>
								<InlineField
									label={t("admin:crm.company.revenue_year")}
									type="number"
									value={org.revenueYear?.toString() ?? ""}
									editable={canManage}
									onSave={(value) =>
										save({
											revenueYear: value.trim()
												? Math.round(Number(value))
												: null,
										})
									}
								/>
								<InlineRow label={t("admin:crm.company.tags")}>
									{canManage ? (
										<TagInput
											value={org.tags}
											onChange={(tags) => saveQuietly({ tags })}
											placeholder={t("admin:crm.tags.placeholder")}
										/>
									) : org.tags.length > 0 ? (
										<div className="flex flex-wrap gap-1">
											{org.tags.map((tag) => (
												<Badge key={tag} variant="secondary">
													{tag}
												</Badge>
											))}
										</div>
									) : (
										"—"
									)}
								</InlineRow>
								<InlineRow label={t("admin:crm.common.owner")}>
									{canManage ? (
										<OwnerSelect
											className="h-8"
											value={org.ownerId}
											onChange={(value) =>
												saveQuietly({ ownerId: value ?? null })
											}
										/>
									) : (
										(data.owner?.name ?? "—")
									)}
								</InlineRow>
								<InlineRow label={t("admin:crm.common.source")}>
									{t(`admin:crm.org_source.${org.source}`, {
										defaultValue: org.source,
									})}
								</InlineRow>
							</dl>
						</CardContent>
					</Card>

					<CompanyGroupCard
						organizationId={org.id}
						group={data.group}
						canManage={canManage}
						wholeGroup={wholeGroup && data.group.memberCount > 1}
						onWholeGroupChange={setWholeGroup}
					/>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-base">
								{t("admin:crm.platform.title")}
							</CardTitle>
							{canManage && !linking && (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setLinking(true)}
								>
									<Plus className="h-4 w-4" />
									{t("admin:crm.platform.link")}
								</Button>
							)}
						</CardHeader>
						<CardContent className="space-y-3">
							{linking && (
								<div className="space-y-2">
									<PlatformOrgPicker
										excludeIds={data.platformLinks.map(
											(linkRow) => linkRow.platformOrganizationId,
										)}
										onPick={(platformOrg) =>
											void runLink("link", platformOrg.id)
										}
									/>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setLinking(false)}
									>
										{t("admin:crm.common.cancel")}
									</Button>
								</div>
							)}
							{data.platformLinks.length === 0 ? (
								<p className="text-muted-foreground text-sm">
									{t("admin:crm.platform.none")}
								</p>
							) : (
								<ul className="divide-y">
									{data.platformLinks.map((linkRow) => (
										<li
											key={linkRow.platformOrganizationId}
											className="flex flex-wrap items-center gap-2 py-2 text-sm"
										>
											<span className="flex-1 font-medium">
												{profile?.canViewSales ? (
													<Link
														to="/sales/$organizationId"
														params={{
															organizationId: linkRow.platformOrganizationId,
														}}
														className="hover:underline"
													>
														{linkRow.name}
													</Link>
												) : (
													linkRow.name
												)}
											</span>
											<PlatformStageBadge stage={linkRow.salesStage} />
											{linkRow.churnRisk && (
												<ToneBadge
													tone="rose"
													icon={TrendingDown}
													title={t("admin:sales.churn_risk")}
												/>
											)}
											<span
												className="text-muted-foreground"
												title={t("admin:crm.platform.mrr")}
											>
												{formatMrr(
													linkRow.mrr.totalCents,
													linkRow.mrr.currencyCode,
													i18n.language,
												)}
											</span>
											<span className="text-muted-foreground text-xs">
												{t(
													`admin:crm.platform.link_source.${linkRow.linkSource}`,
												)}
											</span>
											{canManage && (
												<Button
													variant="ghost"
													size="sm"
													aria-label={t("admin:crm.platform.unlink_named", {
														name: linkRow.name,
													})}
													onClick={() =>
														void runLink(
															"unlink",
															linkRow.platformOrganizationId,
														)
													}
												>
													<Unlink className="h-4 w-4" />
												</Button>
											)}
										</li>
									))}
								</ul>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-base">
								{t("admin:crm.companies.contacts")} ({data.contacts.length})
							</CardTitle>
							{canManage && (
								<div className="flex flex-wrap gap-1">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setExistingContactOpen(true)}
									>
										<Users className="h-4 w-4" />
										{t("admin:crm.company.add_existing_contact")}
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setContactOpen(true)}
									>
										<UserPlus className="h-4 w-4" />
										{t("admin:crm.company.add_contact")}
									</Button>
								</div>
							)}
						</CardHeader>
						<CardContent>
							{data.contacts.length === 0 ? (
								<p className="text-muted-foreground text-sm">
									{t("admin:crm.contacts.empty")}
								</p>
							) : (
								<ul className="divide-y">
									{data.contacts.map((contact) => (
										<li
											key={contact.id}
											className="flex items-center gap-2 py-2 text-sm"
										>
											<Link
												to="/crm/contacts/$contactId"
												params={{ contactId: contact.id }}
												className="font-medium hover:underline"
											>
												{contactName(contact) ?? "—"}
											</Link>
											<span className="flex-1 truncate text-muted-foreground">
												{[contact.title, contact.email]
													.filter(Boolean)
													.join(" · ")}
											</span>
										</li>
									))}
								</ul>
							)}
						</CardContent>
					</Card>

					{canManage && (
						<Card>
							<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle className="text-base">
									{t("admin:crm.deals.title")} ({deals.length})
								</CardTitle>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setDealOpen(true)}
								>
									<Plus className="h-4 w-4" />
									{t("admin:crm.pipeline.new_deal")}
								</Button>
							</CardHeader>
							<CardContent>
								{deals.length === 0 ? (
									<p className="text-muted-foreground text-sm">
										{t("admin:crm.deals.empty")}
									</p>
								) : (
									<ul className="divide-y">
										{deals.map((deal) => (
											<li
												key={deal.id}
												className="flex items-center gap-2 py-2 text-sm"
											>
												<Link
													to="/crm/opportunities/$opportunityId"
													params={{ opportunityId: deal.id }}
													className="flex-1 font-medium hover:underline"
												>
													{deal.title}
												</Link>
												{deal.organizationId !== org.id && (
													<span className="truncate text-muted-foreground text-xs">
														{deal.organizationName}
													</span>
												)}
												{deal.potentialUsers !== null && (
													<span className="text-muted-foreground">
														{t("admin:crm.deals.users", {
															count: deal.potentialUsers,
															formatted: formatNumber(
																deal.potentialUsers,
																i18n.language,
															),
														})}
													</span>
												)}
												<StageBadge stage={deal.stage} />
											</li>
										))}
									</ul>
								)}
							</CardContent>
						</Card>
					)}
					{canManage && (
						<>
							<UpcomingMeetings
								entity={{ entityType: "organization", entityId: org.id }}
							/>
							<TasksPanel
								parent={{ entityType: "organization", entityId: org.id }}
								canManage={canManage}
							/>
						</>
					)}
				</div>
				<ActivityTimeline
					entity={{ entityType: "organization", entityId: org.id }}
					canManage={canManage}
					includeGroup={wholeGroup && data.group.memberCount > 1}
				/>
			</div>

			<ContactFormDialog
				open={contactOpen}
				onOpenChange={setContactOpen}
				defaultCompany={companyRef}
				navigateOnCreate={false}
			/>
			<AddExistingContactDialog
				open={existingContactOpen}
				onOpenChange={setExistingContactOpen}
				company={companyRef}
			/>
			<OpportunityFormDialog
				open={dealOpen}
				onOpenChange={setDealOpen}
				defaultCompany={companyRef}
			/>
			{canAdmin && (
				<MergeDialog
					entityType="organization"
					sourceId={org.id}
					sourceLabel={org.name}
					open={mergeOpen}
					onOpenChange={setMergeOpen}
				/>
			)}
			<AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("admin:crm.company.delete_title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("admin:crm.company.delete_description", { name: org.name })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("admin:crm.common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction onClick={() => void confirmDelete()}>
							{t("admin:crm.company.delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
